// LandingPage.jsx
import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import headImage from "./assets/imgs/headImage.png";
import lawyerImage from "./assets/imgs/lawyerImage.png";
import footerImage from "./assets/imgs/footer.png";
import logo from "./assets/imgs/logo.svg";
import footerLogo from "./assets/imgs/footerLogo.svg";
//import services images
import finanLogo from "./assets/imgs/servicesIcons/finanLogo.svg";
import hrLogo from "./assets/imgs/servicesIcons/hrLogo.svg";
import startLogo from "./assets/imgs/servicesIcons/startLogo.svg";
import steuerLogo from "./assets/imgs/servicesIcons/steuerLogo.svg";
import unterLogo from "./assets/imgs/servicesIcons/unterLogo.svg";
import ContactSection from "./ContactSection";
/* ---------------------------
   Styled components + layout
----------------------------*/
const Container = styled.div`
  width: 100%;
  overflow-x: hidden;
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto,
    "Helvetica Neue", Arial;
  color: #0d1b2a;
  background: #ffffff;
`;

/* NAV */
const Navbar = styled.nav`
  width: 100%;
  max-width: 1200px;
  padding: 18px 40px;
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  z-index: 60;
  box-shadow: 0 4px 12px rgba(13, 27, 42, 0.04);
  box-sizing: border-box;
  margin: auto;
  border: 1px solid #B3E7FB;
  background: rgb(0, 0, 0, 0.2);
  color: white;

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 14px 24px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 12px 16px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 10px 14px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 28px;
  font-size: 15px;
  span {
    position: relative;
    cursor: pointer;
    color: inherit;
    outline: none;
  }
  span::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -3px;
    height: 2px;
    width: 0;
    background: #b3e7fb;
    transition: width 0.25s ease-in-out;
  }
  span:hover {
    color: #b3e7fb;
  }
  span:hover::after {
    width: 100%;
  }
  span:focus-visible {
    outline: 2px solid #b3e7fb;
    outline-offset: 2px;
  }
  span.active {
    color: #b3e7fb;
  }
  span.active::after {
    width: 100%;
  }
  @media (max-width: 1024px) {
    gap: 20px;
    font-size: 14px;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const BurgerButton = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    width: 32px;
    height: 32px;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  }
  span {
    height: 2px;
    width: 100%;
    background: #ffffff;
    border-radius: 2px;
    transition: transform 0.25s ease, opacity 0.25s ease;
  }
  &.open span:nth-child(1) {
    transform: translateY(6px) rotate(45deg);
  }
  &.open span:nth-child(2) {
    opacity: 0;
  }
  &.open span:nth-child(3) {
    transform: translateY(-6px) rotate(-45deg);
  }
  &:focus-visible {
    outline: 2px solid #b3e7fb;
    outline-offset: 3px;
  }
`;

const MobileMenu = styled.div`
  display: none;
  @media (max-width: 768px) {
    position: absolute;
    top: 70px;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(6px);
    padding: 16px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    z-index: 55;
    span {
      color: #ffffff;
      font-size: 15px;
      cursor: pointer;
      outline: none;
    }
    span:focus-visible {
      outline: 2px solid #b3e7fb;
      outline-offset: 2px;
    }
    span.active {
      color: #b3e7fb;
      font-weight: 600;
    }
  }
`;

/* HERO */
const HeroSection = styled.section`
  width: 100%;
  height: 550px;
  background-image: url(${headImage});
  background-size: cover;
  background-position: center 75%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;

  @media (max-width: 768px) {
    height: 380px;
  }

  @media (max-width: 480px) {
    height: 320px;
  }
`;

const HeroText = styled(motion.div)`
  font-size: 34px;
  font-weight: 700;
  line-height: 1.18;
  max-width: 820px;
  padding: 0 18px;
    background: rgba(0, 0, 0, 0.2);
    padding: 10px;
    border-radius: 15px;
  @media (max-width: 1024px) {
    font-size: 30px;
    max-width: 720px;
  }

  @media (max-width: 768px) {
    font-size: 26px;
    max-width: 600px;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    max-width: 360px;
    padding: 0 18px;
  }
`;

/* WELCOME */
const WelcomeBlock = styled.section`
  padding: 64px 20px 20px;
  max-width: 1100px;
  margin: auto;
  display: flex;
  gap: 40px;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 30px;
  }

  @media (max-width: 768px) {
    padding: 48px 16px;
    gap: 24px;
  }

  @media (max-width: 480px) {
    padding: 36px 14px;
    gap: 22px;
  }
`;

const WelcomeText = styled.div`
  max-width: 640px;
  @media (max-width: 900px) {
    max-width: 100%;
  }

  h2 {
    font-size: 36px;
    margin-bottom: 20px;
    color: #133876;
    @media (max-width: 768px) {
      font-size: 28px;
      margin-bottom: 16px;
    }

    @media (max-width: 480px) {
      font-size: 24px;
      margin-bottom: 14px;
    }
  }

  p {
    font-size: 20px;
    line-height: 1.6;
    margin-bottom: 20px;
    color: #0a004d;
    @media (max-width: 768px) {
      font-size: 16px;
      margin-bottom: 18px;
    }

    @media (max-width: 480px) {
      font-size: 15px;
      margin-bottom: 16px;
    }
  }
`;

const VerticalSeperator = styled.div`
  width: 3px;
  height: 300px;
  background: #b3e7fb;
  border-radius: 2px;
  margin: 0 20px;
  align-self: stretch;

  @media (max-width: 900px) {
    display: none;
  }

  @media (max-width: 480px) {
    margin: 0 12px;
    height: 120px;
  }
`;

const CallButton = styled.button`
  padding: 12px 26px;
  background: #133876;
  color: white;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  margin-top: 18px;
  transition: transform 0.22s ease;
  font-size: 20px;

  &:hover {
    transform: translateY(-3px);
  }

  @media (max-width: 900px) {
    margin-top: 12px;
  }

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 16px;
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: 13px 24px;
    font-size: 16px;
  }
`;

/* ACCORDION SECTION (visual only) */
const AccordionWrapper = styled.section`
  padding: 48px 20px;
  background: #d7edff;
  @media (max-width: 768px) {
    padding: 36px 16px;
  }

  @media (max-width: 480px) {
    padding: 32px 14px;
  }
`;

const AccordionCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 22px;
  max-width: 980px;
  margin: 16px auto;
  box-shadow: 0 8px 22px rgba(2, 34, 71, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;

  &.accordion-open {
    background: #d5edf6;
  }

  @media (max-width: 768px) {
    padding: 18px;
    margin: 12px auto;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    margin: 10px auto;
  }

  .accordion-title-number {
    margin: 0;
    font-size: 26px;
    font-weight: 600;
    color: #133876;
    @media (max-width: 768px) {
      font-size: 22px;
    }

    @media (max-width: 480px) {
      font-size: 20px;
    }
  }

  .accordion-title-text {
    margin: 8px 0 0;
    font-size: 20px;
    color: #133876;
    line-height: 1.5;
    padding: 0 12px;
    box-sizing: border-box;
    @media (max-width: 768px) {
      font-size: 18px;
    }
    @media (max-width: 480px) {
      font-size: 16px;
    }
  }
  .accordion-content {
    font-size: 15px;
    color: #5a5365;
    margin: 12px 0 0;
    line-height: 1.5;
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
  > div:last-child {
    font-size: 22px;

    @media (max-width: 768px) {
      align-self: flex-end;
    }

    @media (max-width: 480px) {
      font-size: 20px;
    }
  }
`;

/* HOURS */
const HoursSection = styled.section`
  padding: 46px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: auto;
  @media (max-width: 768px) {
    padding: 38px 16px;
  }

  @media (max-width: 480px) {
    padding: 32px 14px;
  }

  h3 {
    font-size: 36px;
    margin-bottom: 4px;
    color: #205e9f;
    @media (max-width: 768px) {
      font-size: 26px;
      margin-bottom: 12px;
    }

    @media (max-width: 480px) {
      font-size: 22px;
      margin-bottom: 10px;
    }
  }

  p {
    font-size: 17px;
    line-height: 1.5;
    color: #0a004d;
    .day {
      font-weight: bold;
      color: #205e9f;
    }
    @media (max-width: 768px) {
      font-size: 15px;
    }

    @media (max-width: 480px) {
      font-size: 15px;
    }
  }
`;

/* SERVICES GRID */
const ServicesGrid = styled.section`
  padding: 64px 30px;
  margin: 64px auto;
  box-sizing: border-box;
  max-width: 1200px;
  background: #ffffff;
  box-shadow: 0 12px 36px rgba(2, 34, 71, 0.08);
  border-radius: 12px;

  h3 {
    font-size: 34px;
    color: #576974;
    margin-top: 0;
  }
  @media (max-width: 768px) {
    padding: 48px 18px;
  }

  @media (max-width: 480px) {
    padding: 36px 14px;
    margin: 48px auto;
    h3 {
      font-size: 24px;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 22px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 18px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const GridCard = styled(motion.div)`
  background: #f7fbff;
  padding: 22px;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(2, 34, 71, 0.04);
  @media (max-width: 768px) {
    padding: 18px;
  }

  @media (max-width: 480px) {
    padding: 16px;
  }

  h4 {
    color: #133876;
    font-size: 20px;
    text-align: center;
    margin-bottom: 6px;
    @media (max-width: 480px) {
      font-size: 18px;
    }
  }

  p,
  ul {
    color: #5c6b77;
    font-size: 15px;
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
`;

/* ABOUT */
const AboutSection = styled.section`
  background: #d7edff;
  padding: 72px 20px;

  .about-wrapper {
    max-width: 1200px;
    display: flex;
    margin: auto;
    justify-content: space-between;

    @media (max-width: 900px) {
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 0;
      gap: 24px;
    }
  }
  .about-text {
    width: 60%;
    @media (max-width: 900px) {
      width: 100%;
    }
  }
  h3 {
    color: #133876;
    font-size: 42px;
    margin: 0 0 12px;
    @media (max-width: 768px) {
      font-size: 32px;
    }
    @media (max-width: 480px) {
      font-size: 24px;
    }
  }
  p {
    color: #0a004d;
    font-size: 18px;
    line-height: 1.5;
    margin-bottom: 0;
    @media (max-width: 480px) {
      font-size: 15px;
    }
  }
  .image-container {
    width: 30%;
    @media (max-width: 900px) {
      width: 60%;
    }
    @media (max-width: 480px) {
      width: 80%;
    }
  }
  @media (max-width: 900px) {
    padding: 48px 16px;
    gap: 24px;
  }

  @media (max-width: 480px) {
    padding: 36px 14px;
    gap: 20px;
  }
`;

const AboutImage = styled.img`
  width: 100%;
`;

/* FOOTER */
const Footer = styled.footer`
  background: #0d1b2a;
  color: white;
  padding: 80px 20px;
  text-align: center;
  background-image: url(${footerImage});

  @media (max-width: 768px) {
    padding: 52px 18px;
  }

  @media (max-width: 480px) {
    padding: 40px 14px;
  }

  h4 {
    font-size: 34px;
    margin: 0 0 8px;
    @media (max-width: 768px) {
      font-size: 28px;
    }
    @media (max-width: 480px) {
      font-size: 22px;
    }
  }

  p {
    font-size: 18px;
    @media (max-width: 768px) {
      font-size: 16px;
    }
    @media (max-width: 480px) {
      font-size: 15px;
    }
  }

  button {
    padding: 13px 22px;
    font-size: 17px;
    transition: transform 0.22s ease;

    &:hover {
      transform: translateY(-3px);
    }

    @media (max-width: 768px) {
      padding: 12px 20px;
      font-size: 15px;
    }

    @media (max-width: 480px) {
      padding: 10px 18px;
      font-size: 14px;
    }
  }
`;
const FooterLinks = styled.div`
  background: #133876;
  padding: 10px 20px;
  > div {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: white;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
  }
  .footer-logo-container {
    width: 15%;
    img {
      width: 100%;
    }
    @media (max-width: 768px) {
      width: 30%;
    }
    @media (max-width: 480px) {
      width: 40%;
    }
  }
  .footer-link {
    color: white;
    text-decoration: none;
    position: relative;
  }
  .footer-link::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -2px;
    height: 2px;
    width: 0;
    background: #b3e7fb;
    transition: width 0.25s ease-in-out;
  }
  .footer-link:hover {
    color: #b3e7fb;
  }
  .footer-link:hover::after {
    width: 100%;
  }
`;
/* ---------------------------
   Animations
----------------------------*/
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

/* =============================
MAIN PAGE COMPONENT
============================= */
/* ACCORDION FUNCTIONAL */

const AccordionSection = () => {
  const [openIndex, setOpenIndex] = React.useState(null);
  const items = [
    {
      title: "Treuhand",
      text: "Wir sind spezialisiert auf die Unternehmensgründungen, Finanz- und Lohnbuchhaltung, die Erstellung von Jahresabschlüssen sowie die Unterstützung bei steuerlichen Angelegenheiten. Unser erfahrenes Team steht Ihnen zur Seite und arbeitet eng mit Ihnen zusammen, um Ihre geschäftlichen Anforderungen zu verstehen und massgeschneiderte Lösungen anzubieten. Wir legen grossen Wert auf Transparenz, Vertraulichkeit und professionelle Dienstleistungen, um Sie bei der optimalen Verwaltung Ihres Unternehmens zu unterstützen.",
    },
    {
      title: "Steuern",
      text: "Unsere erfahrenen Experten stehen Ihnen zur Seite, um die komplexen steuerlichen Anforderungen zu bewältigen und Ihre finanzielle Situation optimal zu gestalten. Wir bieten effiziente Steuerplanung,-strukturierung und -optimierung für Unternehmen und Privatpersonen. Vertrauen Sie auf unsere Fachkenntnisse, um Ihre Steuerbelastung zu minimieren und gleichzeitig die gesetzlichen Bestimmungen einzuhalten.",
    },
    {
      title: "Services",
      text: "Wir unterstützen Sie nicht nur in den Bereichen Buchhaltung & Steuern, sondern auch in vielen weiteren Bereichen. Wir verstehen die individuellen Bedürfnisse unserer Kunden und bieten massgeschneiderte Lösungen, um Sie bei der Optimierung ihrer finanziellen Angelegenheiten zu unterstützen. Unsere erfahrenen Treuhänder stehen Ihnen mit ihrem Fachwissen und ihrer Fachkenntnis zur Verfügung, um einen effizienten und professionellen Service zu gewährleisten.",
    },
  ];
  return (
    <AccordionWrapper>
      {items.map((item, i) => (
        <AccordionCard
          key={i}
          onClick={() => setOpenIndex(openIndex === i ? null : i)}
          className={openIndex === i ? "accordion-open" : ""}
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          style={{
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              className="accordion-title"
              style={{ display: "flex", alignItems: "center", gap: 12 }}
            >
              <p className="accordion-title-number">
                {String(i + 1).padStart(2, "0")}
              </p>{" "}
              <p className="accordion-title-text">{item.title}</p>
            </div>
            <motion.div animate={{ rotate: openIndex === i ? 45 : 0 }}>
              <span
                style={{
                  fontSize: "28px",
                  width: "40px",
                  height: "40px",
                  display: "block",
                  textAlign: "center",
                  borderRadius: "50%",
                  background: "#133876",
                  color: "white",
                }}
              >
                +
              </span>
            </motion.div>
          </div>
          <motion.div
            layout
            initial={false}
            animate={{
              opacity: openIndex === i ? 1 : 0,
              marginTop: openIndex === i ? 12 : 0,
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: "hidden", width: "100%" }}
          >
            {openIndex === i && (
              <p className="accordion-content">{item.text}</p>
            )}
          </motion.div>
        </AccordionCard>
      ))}
    </AccordionWrapper>
  );
};

export default function LandingPage() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState("top");
  const firstNameRef = React.useRef(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState("");
  const [submitStatus, setSubmitStatus] = React.useState(null); // "success" | "error" | null

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const form = e.target;
    const formData = new FormData();
    formData.append("first_name", form.first_name.value.trim());
    formData.append("last_name", form.last_name.value.trim());
    formData.append("company", form.company.value.trim());
    formData.append("email", form.email.value.trim());
    formData.append("phone", form.phone.value.trim());
    formData.append("description", form.description.value.trim());

    try {
      setIsSubmitting(true);
      setSubmitMessage("");
      setSubmitStatus(null);
      const res = await fetch("https://cms.sfm-trust.ch/api/contact/send", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        throw new Error("Request failed");
      }

      setSubmitStatus("success");
      setSubmitMessage("Vielen Dank. Ihre Nachricht wurde erfolgreich gesendet.");
      form.reset();
      if (firstNameRef.current) {
        firstNameRef.current.focus();
      }
    } catch (err) {
      console.error(err);
      setSubmitStatus("error");
      setSubmitMessage(
        "Beim Senden ist ein Fehler aufgetreten. Bitte überprüfen Sie Ihre Eingaben oder versuchen Sie es erneut."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // lock body scroll when mobile menu is open
  React.useEffect(() => {
    if (mobileOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [mobileOpen]);

  // observe sections for active state
  React.useEffect(() => {
    const sectionIds = ["top", "services", "about", "contact"];
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!("IntersectionObserver" in window) || elements.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        root: null,
        threshold: 0.4,
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, []);

  const handleNavClick = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  const scrollToContactAndFocus = () => {
    const contactEl = document.getElementById("contact");
    if (contactEl) {
      contactEl.scrollIntoView({ behavior: "smooth" });
    }
    if (firstNameRef.current) {
      firstNameRef.current.focus();
    }
  };

  const services = [
    {
      title: "Unternehmensberatung",
      detail: (
        <>
          <p>
            Wir helfen Jungunternehmer/innen, ihre Firma zu gründen und
            übernehmen für sie lästige administrative Tätigkeiten. Unser
            Spezialisten-Netzwerk von Juristen sowie Steuer-und Treuhandexperten
            sorgt zudem dafür, dass alles korrekt abgewickelt wird.
          </p>
          <ul>
            <li>Beratung von Startups</li>
            <li>Unternehmensgründungen</li>
            <li>Wirtschaftlichkeitsberechnungen, Deckungsbeitragsrechnungen</li>
          </ul>
        </>
      ),
      icon: unterLogo,
    },
    {
      title: "Start - Ups",
      detail: (
        <>
          <p>
            Sie konzentrieren sich auf Ihr Start-up, indem Sie sich Ihren
            Kernkompetenzen widmen, Ihr Produkt oder Ihre Lösung
            weiterentwickeln und neue Kundinnen und Kunden gewinnen. Wir kümmern
            uns um Ihre Buchhaltung und um Steuerfragen.
          </p>
          <p>
            Wenn Ihr Unternehmen wächst, begleiten wir Sie auf dieser spannenden
            Reise und unterstützen Sie bei Bedarf bei der Buchführung, der
            Rechnungsstellung und der Lohnadministration sowie bei Ausgaben- und
            Steuerangelegenheiten.
          </p>
        </>
      ),
      icon: startLogo,
    },
     {
      title: "Steuerberatung",
      detail: (
        <>
          <p>
            Wir übernehmen für Sie die ganze steuerliche Administration und
            vertreten Sie im Verkehr mit den zuständigen Stellen und den
            Rechtsmittelinstanzen.
          </p>
          <ul>
            <li>Steuererklärungen für natürliche und juristische Personen</li>
            <li>Steuervertretungen</li>
            <li>MWST-Beratung und Erstellung der Abrechnungen</li>
            <li>
              Finanz- und Vorsorgeplanung angepasst auf Ihre persönlichen
              Lebensumstände
            </li>
          </ul>
        </>
      ),

      icon: steuerLogo,
    },   
    {
      title: "HR Personalwesen / Lohnbuchhaltung",
      detail: (
        <>
          <p>
            Die Führung der Lohnbuchhaltung wird immer komplizierter und
            zeitaufwendiger. Ohne einen kompetenten und erfahrenen Partner ist
            dies fast nicht mehr zu realisieren.
            <br />
            <br />
            Ein pünktlich und korrekt ausgezahlter Lohn ist Grundvoraussetzung
            für das Vertrauensverhältnis zwischen Ihnen und Ihren
            Mitarbeitenden. Unsere Leistungen im Zusammenhang mit der Lohn- und
            Personaladministration geht aber weit darüber hinaus.
          </p>
          <ul>
            <li>Saläradministration von A-Z</li>
            <li>monatlichen Lohnabrechnungen</li>
            <li>jährliche Sozialversicherungsdeklarationen</li>
            <li>
              Personalmutationen / An- und Abmeldungen bei sämtlichen
              Personenversicherungen
            </li>
            <li>Erstellen von Arbeitsverträgen</li>
            <li>
              Ausfüllen von Quellensteuerabrechnungen und Bewilligungsformularen
            </li>
            <li>Lohnausweise</li>
            <li>Lohnbuchhaltung</li>
          </ul>
        </>
      ),

      icon: hrLogo,
    },
    {
      title: "Finanzbuchhaltung",
      detail: (
        <>
          <p>
            Zusammen mit unseren Kunden wird eine optimale Organisation der
            Buchhaltung erarbeitet und abgeklärt, welche Aufgaben er selbst
            erledigt, und welche Aufgaben er uns übertragen möchte.
            <br />
            <br />
            Wir führen die Buchhaltung (Finanz- und/oder Lohnbuchhaltung) für
            Ihr Unternehmen bei Ihnen vor Ort oder in unserem Haus kompetent und
            effizient.
          </p>
          <ul>
            <li>Nachführen der Finanzbuchhaltung</li>
            <li>
              Nachführen Ihrer Finanzbuchhaltung auf Ihrem eigenen System bei
              Ihnen Vorort.
            </li>
            <li>Debitorenbuchhaltung inkl. Inkassowesen</li>
            <li>Kreditorenbuchhaltung</li>
            <li>Erstellen des Jahresabschlusses</li>
            <li>Erstellung von Zwischenabschlüssen und Monatsreporting</li>
            <li>Controlling sämtlicher Arbeiten in der Finanzbuchhaltung</li>
            <li>Einführung einer Betriebsbuchhaltung und Unterstützung</li>
            <li>Auswertungen, Statistiken, Analysen</li>
          </ul>
        </>
      ),
      icon: finanLogo,
    },

  ];
  const getCurrentYear = () => new Date().getFullYear();
  const currentYear = getCurrentYear();

  return (
    <Container>
      {/* NAV */}
      <Navbar>
        <img src={logo} alt="logo" height="36" />
        <NavLinks>
          <span
            onClick={() => handleNavClick("top")}
            className={activeSection === "top" ? "active" : ""}
          >
            Startseite
          </span>
          <span
            onClick={() => handleNavClick("services")}
            className={activeSection === "services" ? "active" : ""}
          >
            Dienstleistungen
          </span>
          <span
            onClick={() => handleNavClick("about")}
            className={activeSection === "about" ? "active" : ""}
          >
            Über uns
          </span>
          <span
            onClick={() => handleNavClick("contact")}
            className={activeSection === "contact" ? "active" : ""}
          >
            Kontakt
          </span>
        </NavLinks>
        <BurgerButton
          className={mobileOpen ? "open" : ""}
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Navigation öffnen oder schliessen"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          <span />
          <span />
          <span />
        </BurgerButton>
      </Navbar>
      {mobileOpen && (
        <MobileMenu id="mobile-menu">
          <span
            onClick={() => handleNavClick("top")}
            className={activeSection === "top" ? "active" : ""}
          >
            Startseite
          </span>
          <span
            onClick={() => handleNavClick("services")}
            className={activeSection === "services" ? "active" : ""}
          >
            Dienstleistungen
          </span>
          <span
            onClick={() => handleNavClick("about")}
            className={activeSection === "about" ? "active" : ""}
          >
            Über uns
          </span>
          <span
            onClick={() => handleNavClick("contact")}
            className={activeSection === "contact" ? "active" : ""}
          >
            Kontakt
          </span>
        </MobileMenu>
      )}

      {/* HERO */}
      <HeroSection id="top">
        <HeroText
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
        >
          Verlässlichkeit in jeder Zahl,
          <br />
          <strong>Sicherheit in jeder Entscheidung</strong>
        </HeroText>
      </HeroSection>

      {/* WELCOME */}
      <WelcomeBlock>
        <WelcomeText>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            Willkommen bei <br />
            <strong>Swiss Financial Management,</strong>
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            Ihrem vertrauenswürdigen Partner für Treuhanddienstleistungen.
            Unsere Experten unterstützen Sie bei der effizienten Verwaltung
            Ihrer Finanzen und sorgen für eine sichere und transparente
            Abwicklung. Verlassen Sie sich auf unser Fachwissen und unsere
            langjährige Erfahrung.
          </motion.p>
        </WelcomeText>
        <VerticalSeperator />
        <CallButton>
          <a
            href="tel:+41 79 202 20 91"
            style={{ color: "white", textDecoration: "none" }}
          >
            JETZT ANRUFEN
          </a>
        </CallButton>
      </WelcomeBlock>

      {/* ACCORDION VISUAL */}
      <AccordionSection />

      {/* SERVICES GRID */}
      <ServicesGrid id="services">
        <motion.h3
          style={{ textAlign: "center", marginBottom: 28 }}
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
        >
          Unsere Dienstleistungen
        </motion.h3>
        <Grid>
          {services.map((s, idx) => (
            <GridCard
              key={s}
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.06 }}
            >
              <div style={{ height: 48, marginBottom: 12 }}>
                {/* placeholder icon circle */}
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: "#eef7ff",
                    margin: "0 auto",
                    display: "flex",
                    justifyContent: "center",
                    alignContent: "center",
                  }}
                >
                  <img
                    style={{ width: "50%" }}
                    src={s.icon}
                    alt={`${s.title} icon`}
                  />
                </div>
              </div>
              <h4 style={{ marginBottom: 8 }}>{s.title}</h4>
              <p style={{ fontSize: 14, color: "#5c6b77" }}>{s.detail}</p>
            </GridCard>
          ))}
        </Grid>
      </ServicesGrid>

      {/* ABOUT */}
      <AboutSection id="about">
        <div className="about-wrapper">
          <motion.div
            initial="hidden"
            whileInView="visible"
            className="about-text"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <h3>ÜBER UNS</h3>
            <p>
              Wir sind Swiss Financial Management, ein junges Unternehmen im
              Bereich Treuhanddienstleistungen. Unser ausgebildetes und
              engagiertes Team verfügt über umfangreiche Erfahrung und Expertise
              in allen Bereichen der Treuhand. Bei uns stehen die individuellen
              Bedürfnisse unserer Kunden im Mittelpunkt, und wir bieten ihnen
              massgeschneiderte Lösungen zur Unterstützung ihrer geschäftlichen
              und persönlichen finanziellen Angelegenheiten.
              <br />
              <br />
              Wir legen grossen Wert auf eine vertrauensvolle Zusammenarbeit und
              pflegen langjährige Partnerschaften mit unseren Kunden. Unser
              Netzwerk umfasst auch eine Vielzahl von Partnern aus verschiedenen
              Bereichen, die uns unterstützen und uns ermöglichen, unseren
              Kunden ein breites Spektrum an Dienstleistungen anzubieten. Ob
              Buchhaltung, Steuerberatung oder Unternehmensgründung - bei Swiss
              Financial Management sind Sie in guten Händen. Wir stehen Ihnen
              gerne zur Verfügung und helfen Ihnen dabei, Ihre finanziellen
              Ziele zu erreichen.
            </p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            className="image-container"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <AboutImage src={lawyerImage} alt="profile" />
          </motion.div>
        </div>
      </AboutSection>



      {/* HOURS */}
      <HoursSection>
        <motion.h3
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
        >
          Öffnungszeiten
        </motion.h3>
        <motion.p>
          Kontaktieren Sie uns gerne während unserer Öffnungszeiten für alle
          Ihre finanziellen Anliegen.
          <br />
          Wir freuen uns darauf, Ihnen weiterzuhelfen.
        </motion.p>
        <div>
          <motion.p
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <span className="day">Montag - Donnerstag</span> <br /> 08:00 -
            17:00
          </motion.p>
          <motion.p
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <span className="day">Freitag</span>
            <br /> 09:00 - 17:00
          </motion.p>
          <motion.p
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <span className="day">Samstag- Sonntag</span>
            <br /> Geschlossen
          </motion.p>
        </div>
      </HoursSection>

      {/* CONTACT */}
      <ContactSection
        firstNameRef={firstNameRef}
        isSubmitting={isSubmitting}
        submitMessage={submitMessage}
        submitStatus={submitStatus}
        onSubmit={handleSubmitContact}
      />

      {/* FOOTER */}
      <Footer>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h4>Haben Sie Fragen?</h4>
          <p>Bitte zögern Sie nicht, uns zu kontaktieren.</p>
          <div style={{ marginTop: 18 }}>
            <button
              onClick={scrollToContactAndFocus}
              style={{
                background: "#ffffff",
                color: "#0d1b2a",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
              }}
            >
              Kontakt
            </button>
          </div>
        </div>
      </Footer>
      {/* FOOTER LINKS */}
      <FooterLinks>
        <div>
          <div className="footer-logo-container">
            <img src={footerLogo} alt="footer logo" />
          </div>
          <NavLinks>
            <span onClick={() => handleNavClick("top")}>
              Startseite
            </span>
            <span onClick={() => handleNavClick("services")}>
              Dienstleistungen
            </span>
            <span onClick={() => handleNavClick("about")}>
              Über uns
            </span>
            <span onClick={() => handleNavClick("contact")}>
              Kontakt
            </span>
          </NavLinks>
          <p>
            All Rights Reserved by{" "}
            <a
              href="https://www.metamorph-sd.com"
              target="_blank"
              rel="noreferrer"
              className="footer-link"
            >
              MSD
            </a>{" "}
            {currentYear}
          </p>
        </div>
      </FooterLinks>
    </Container>
  );
}
